import { useState } from "react";
import { Form, Input, Button, Checkbox, ConfigProvider, message } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { User, Wrench, ClipboardCheck, FileText } from "lucide-react";
import axios from "axios";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import useAuthStore from "@/store/useAuthStore";
import userServices from "@/services/auth.service";
import { apiUrl } from "@/services/api";
import { mapBackendRole } from "@/config/roles";
import edkasLogo from "@/assets/logo/edkaslogo.png";
import ForgotPassword from "./ForgotPassword";

const THEME_BLUE = "#1858EF";
const DEFAULT_LOCATION = { latitude: 11.7213378, longitude: 79.7743196 };

// Placeholder photo (container ship). Replace with your own asset, e.g.
// import heroImage from "@/assets/login-hero.jpg";
const HERO_IMAGE =
  "https://images.unsplash.com/photo-1578575437130-527eed3abbec?auto=format&fit=crop&w=1600&q=80";

// The three apps that make up BlueFlash.
const FEATURES = [
  {
    icon: Wrench,
    title: "PMS",
    desc: "Planned maintenance, jobs and running hours for every vessel.",
  },
  {
    icon: ClipboardCheck,
    title: "Inspection",
    desc: "Digital inspections and reports, ready for ship or shore.",
  },
  {
    icon: FileText,
    title: "Document Management",
    desc: "Certificates, manuals and records in one controlled place.",
  },
];

// Module level so FingerprintJS loads once, not on every render.
let fpPromise = null;
const getBrowserFingerprint = async () => {
  if (!fpPromise) fpPromise = FingerprintJS.load();
  const fp = await fpPromise;
  const { visitorId } = await fp.get();
  return visitorId;
};

const getDeviceType = () => {
  const ua = navigator.userAgent;
  const isMobile =
    navigator.userAgentData?.mobile ??
    /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|Mobile/i.test(ua);
  return isMobile ? "MOBILE" : "DESKTOP";
};

const getGeoLocation = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) =>
        resolve({ latitude: coords.latitude, longitude: coords.longitude }),
      (error) => {
        console.warn("Location unavailable:", error.message);
        resolve(null);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  });

const getErrorMessage = (err) => {
  const status = err?.response?.status;
  const backendMessage = err?.response?.data?.message;
  const errorCode = err?.response?.data?.error;

  switch (status) {
    case 404:
      return "Email not correct";
    case 401:
      if (errorCode === "session_expired")
        return "Your session expired before login could complete. Please try again.";
      if (errorCode === "jwt_expired")
        return "Login token expired — please try again.";
      return backendMessage || "Incorrect password";
    case 403:
      return backendMessage || "Access denied. Contact your administrator.";
    case 400:
      return backendMessage || "Invalid login request";
    default:
      return backendMessage || "Server not reachable";
  }
};

// Rendered by <App /> whenever there is no valid session. On success the
// store flips to authenticated and <App /> swaps in the launcher — no
// navigation needed.
export default function Login() {
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("login"); // "login" | "forgot"
  const login = useAuthStore((s) => s.login);

  const handleLogin = async (values) => {
    setLoading(true);
    try {
      const [geo, fingerprint] = await Promise.all([
        getGeoLocation(),
        getBrowserFingerprint(),
      ]);
      const location = geo ?? DEFAULT_LOCATION;

      const res = await userServices.loginapi({
        username: values.username,
        password: values.password,
        fingerprint,
        origin: window.location.origin,
        platform: navigator.platform,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        deviceType: getDeviceType(),
        latitude: location.latitude,
        longitude: location.longitude,
      });

      const response = res.data;
      if (!response.success) {
        message.error(response.message);
        return;
      }

      let vendorinfo = null;
      if (response.role === "VENDOR") {
        const vendor = await axios.get(
          `${apiUrl}/vendors/vendorinfobyuserid/${response.user_id}`,
          { headers: { Authorization: `Bearer ${response.accessToken}` } },
        );
        vendorinfo = vendor?.data?.data ?? null;
      }

      login(
        response.accessToken,
        response.user_id || "",
        mapBackendRole(response.role),
        response.username,
        response.ship_id || "",
        response.ownerId || "",
        response.owner_group_id || response.ownergroup_id || "",
        response.fleet_id || "",
        vendorinfo,
        response.sessionId || null,
        [response.latitude ?? null, response.longitude ?? null],
      );
    } catch (err) {
      console.error("Login error", err);
      message.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const overlay = {
    background: `linear-gradient(160deg, ${THEME_BLUE}e6 0%, ${THEME_BLUE}99 50%, #000000aa 100%)`,
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: THEME_BLUE,
          colorLink: THEME_BLUE,
          borderRadius: 8,
          fontSize: 14,
        },
        components: {
          Input: { controlHeight: 42 },
          Button: { controlHeight: 44, fontWeight: 600 },
        },
      }}
    >
      <div className="grid min-h-screen w-full grid-cols-1 bg-white lg:grid-cols-2">
        {/* Mobile / tablet banner (hidden on desktop) */}
        <div className="relative h-52 sm:h-60 lg:hidden">
          <img
            src={HERO_IMAGE}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0" style={overlay} />
          <div className="relative z-10 flex h-full flex-col items-center justify-center px-6 pb-6 text-center text-white">
            <p className="text-lg font-semibold">Maintenance · Inspection · Documents</p>
            <p className="mt-1 text-sm text-white/80">One platform for your fleet</p>
          </div>
        </div>

        {/* Desktop: hero image with centered content */}
        <div className="relative hidden lg:block">
          <img
            src={HERO_IMAGE}
            alt="Ship at sea"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0" style={overlay} />

          <div className="relative z-10 flex h-full flex-col items-center justify-between p-10 text-center text-white xl:p-12">
            <div aria-hidden="true" />

            <div className="flex w-full max-w-xl flex-col items-center">
              <h2 className="mb-3 text-3xl font-semibold leading-tight xl:text-4xl">
                Maintenance, inspections and documents. One platform.
              </h2>
              <p className="mb-8 max-w-md text-base text-white/80">
                Everything your fleet needs to stay compliant and running, in a single sign-in.
              </p>

              <ul className="grid w-full grid-cols-1 gap-3 xl:grid-cols-3">
                {FEATURES.map(({ icon: Icon, title, desc }) => (
                  <li
                    key={title}
                    className="flex flex-col items-center rounded-xl border border-white/15 bg-white/10 p-4 text-center backdrop-blur-sm"
                  >
                    <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-lg bg-white/15">
                      <Icon size={22} />
                    </span>
                    <p className="mb-1 text-sm font-semibold">{title}</p>
                    <p className="text-xs leading-relaxed text-white/75">{desc}</p>
                  </li>
                ))}
              </ul>
            </div>

            <p className="text-sm text-white/60">
              © {new Date().getFullYear()} BlueFlash Platform. All rights reserved.
            </p>
          </div>
        </div>

        {/* Login form */}
        <div className="relative -mt-6 flex items-start justify-center rounded-t-3xl bg-white px-5 pb-10 pt-8 sm:px-12 lg:mt-0 lg:items-center lg:rounded-none lg:py-10">
          <div className="w-full max-w-sm">
            <div className="mb-6 text-center lg:mb-8">
              <img
                src={edkasLogo}
                alt="BlueFlash"
                className="mx-auto mb-3 h-16 w-16 rounded-2xl object-contain"
              />
              <h1 className="mb-1 text-2xl font-semibold" style={{ color: THEME_BLUE }}>
                BlueFlash
              </h1>
              <p className="text-sm text-gray-500">
                {view === "login" ? "Sign in to your account" : "Reset your password"}
              </p>
            </div>

            {view === "forgot" ? (
              <ForgotPassword onBack={() => setView("login")} />
            ) : (
            <Form
              name="login"
              layout="vertical"
              requiredMark={false}
              initialValues={{ remember: true }}
              onFinish={handleLogin}
              disabled={loading}
            >
              <Form.Item
                name="username"
                rules={[{ required: true, whitespace: true, message: "Please input your username!" }]}
              >
                <Input
                  prefix={<User size={16} className="text-gray-400" />}
                  placeholder="Username"
                  autoComplete="username"
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[{ required: true, message: "Please input your password!" }]}
              >
                <Input.Password
                  prefix={<LockOutlined className="text-gray-400" />}
                  placeholder="Password"
                  autoComplete="current-password"
                />
              </Form.Item>

              <div className="mb-5 flex flex-wrap items-center justify-between gap-2">
                <Form.Item name="remember" valuePropName="checked" noStyle>
                  <Checkbox>Remember me</Checkbox>
                </Form.Item>
                <button
                  type="button"
                  className="text-sm font-medium hover:underline"
                  style={{ color: THEME_BLUE }}
                  onClick={() => setView("forgot")}
                >
                  Forgot password?
                </button>
              </div>

              <Button type="primary" htmlType="submit" block loading={loading}>
                {loading ? "Signing in…" : "Sign In"}
              </Button>
            </Form>
            )}
          </div>
        </div>
      </div>
    </ConfigProvider>
  );
}